/**
 * Daten-Lade- und Build-Logik für den Flotten-Tab.
 * Kapselt Supabase-Aufrufe (tick-planet, build-ship) und Dev-Mode-Fallback.
 */
import type { SupabaseClient } from './SupabaseClient';
import type { PlanetInfo } from '../world/PlayerWorldTypes';
import type { BuildingType } from '../config/BuildingTypes';
import { ALL_BUILDING_TYPES } from '../config/BuildingTypes';

const SHIP_COLUMNS = [
  'scout_count', 'fighter_count', 'bucaneer_count', 'battleship_count',
  'icss_count', 'carrier_count', 'receptor_count', 'colonyship_count',
] as const;

type ShipComposition = Record<string, number>;

export interface FleetLoadResult {
  composition: number[];
  levels: Map<BuildingType, number>;
}

export function parseShipsFromResponse(ships: ShipComposition | null): number[] {
  const composition = [0, 0, 0, 0, 0, 0, 0, 0];
  if (!ships) return composition;
  for (let i = 0; i < SHIP_COLUMNS.length; i++) {
    composition[i] = (ships[SHIP_COLUMNS[i]] as number) ?? 0;
  }
  return composition;
}

export function devLoad(
  devShips: Map<string, number[]>,
  planetId: string,
): FleetLoadResult {
  if (!devShips.has(planetId)) {
    devShips.set(planetId, [0, 0, 0, 0, 0, 0, 0, 0]);
  }
  const composition = devShips.get(planetId)!;
  const levels = new Map<BuildingType, number>();
  for (const t of ALL_BUILDING_TYPES) levels.set(t, 0);
  return { composition, levels };
}

export async function loadFleetData(
  supabase: SupabaseClient,
  planet: PlanetInfo,
): Promise<FleetLoadResult & { error?: string }> {
  const composition = [0, 0, 0, 0, 0, 0, 0, 0];
  const levels = new Map<BuildingType, number>();

  const { data, error } = await supabase.functions.invoke('tick-planet', {
    body: { planet_id: planet.id },
  });
  if (error || !data) return { composition, levels, error: 'Flotte konnte nicht geladen werden' };

  if (data.resources) {
    planet.feronium = data.resources.feronium;
    planet.chrysalium = data.resources.chrysalium;
    planet.neurogel = data.resources.neurogel;
  }
  if (data.buildings) {
    for (const b of data.buildings) {
      levels.set(b.type as BuildingType, b.level);
    }
  }
  return { composition: parseShipsFromResponse(data.ships), levels };
}

export async function buildShip(
  supabase: SupabaseClient,
  planet: PlanetInfo,
  shipType: number,
): Promise<{ composition: number[]; error?: string }> {
  const { data, error } = await supabase.functions.invoke('build-ship', {
    body: { planet_id: planet.id, ship_type: shipType, count: 1 },
  });

  if (error || !data?.success) {
    const ctx = error && 'context' in error
      ? (error as { context: { error?: string } }).context : null;
    return {
      composition: [0, 0, 0, 0, 0, 0, 0, 0],
      error: ctx?.error ?? (data?.error as string) ?? 'Bau fehlgeschlagen',
    };
  }

  if (data.resources) {
    planet.feronium = data.resources.feronium;
    planet.chrysalium = data.resources.chrysalium;
    planet.neurogel = data.resources.neurogel;
  }

  return { composition: parseShipsFromResponse(data.ships) };
}
